import { FC, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
    Sheet,
    SheetClose,
    SheetContent,
    SheetDescription,
    SheetFooter,
    SheetHeader,
    SheetTitle,
} from '@/components/ui/sheet';
import MoveLeftIcon from '@/components/icons/MoveLeftIcon';
import { Toaster } from 'sonner';
import { cn } from '@/lib/utils';
import useMediaQuery from '@/hooks/useMediaQuery';
import { AppConfig } from '@/config/app.config';
import Cancel_01_Icon from '@/components/icons/Cancel_01_Icon';
import ECreateDetailsCopyItem from '@/components/everridge/ECreateDetailsCopyItem';
import { useUserDetailsApi } from '@/features/superAdmin/features/users/hooks/useUserDetailsApi';
import {
    UserUpdateManageSchema,
    UserCreateManageSchema,
    UserUpdateFormData,
    UserCreateFormData,
} from '@/features/superAdmin/features/users/validators/UserManageValidator';
import { useForm, Control, UseFormSetValue } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import {
    CreateUserRequestDto,
    UpdateUserRequestDto,
} from '@/features/superAdmin/features/users/types/superAdmin-users-dto';
import ETextInput from '@/components/everridge/ETextInput';
import { Button } from '@/components/ui/button';
import ECheckboxInput from '@/components/everridge/ECheckboxInput';
import UserTenantRoles from '@/features/superAdmin/features/users/components/UserTenantRoles';
import UserPasswordField from '@/features/superAdmin/features/users/components/UserPasswordField';
import UserSuperAdminConfirmDialog from '@/features/superAdmin/features/users/components/UserSuperAdminConfirmDialog';
import UserSetPasswordDialog from '@/features/superAdmin/features/users/components/UserSetPasswordDialog';
import UserSendPasswordRecoveryDialog from '@/features/superAdmin/features/users/components/UserSendPasswordRecoveryDialog';
import EButton from '@/components/everridge/EButton';
import EToast from '@/components/everridge/EToast';
import { useSignIn } from '@/features/identity/hooks/useSignIn';
import { AuthHelper } from '@/utils/auth.helper';

type Props = {
    id?: string;
    onClose: (state: boolean) => void;
};

const UserManage: FC<Props> = ({ id, onClose }) => {
    const [isOpen, setIsOpen] = useState(true);
    const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
    const [setPasswordDialogOpen, setSetPasswordDialogOpen] = useState(false);
    const [recoveryDialogOpen, setRecoveryDialogOpen] = useState(false);
    const initialIsSuperAdminRef = useRef(false);

    const isMobile = useMediaQuery(AppConfig.mediaQueries.mobile);
    const { t } = useTranslation();
    const navigate = useNavigate();
    const signIn = useSignIn();

    const {
        userDetailsData,
        createUser,
        updateUser,
        impersonateUser,
        setUserPassword,
        sendPasswordRecoveryEmail,
        isCreatingUser,
        isUpdatingUser,
        isImpersonatingUser,
        isSettingUserPassword,
        isSendingPasswordRecoveryEmail,
    } = useUserDetailsApi({
        id,
    });

    const { control, handleSubmit, reset, setValue } = useForm<UserUpdateFormData | UserCreateFormData>({
        resolver: zodResolver(id ? UserUpdateManageSchema : UserCreateManageSchema),
        defaultValues: {
            tenantRoles: [],
            isSuperAdmin: false,
        },
    });

    useEffect(() => {
        if (userDetailsData) {
            initialIsSuperAdminRef.current = userDetailsData.isSuperAdmin ?? false;
            reset({
                userName: userDetailsData.userName,
                lockoutEnabled: userDetailsData.lockoutEnabled,
                emailConfirmed: userDetailsData.emailConfirmed,
                isSuperAdmin: userDetailsData.isSuperAdmin ?? false,
                tenantRoles: userDetailsData.tenantRoles || [],
            });
        }
    }, [userDetailsData, reset]);

    const handleClose = () => {
        setIsOpen(false);
        setTimeout(onClose, 300);
    };

    const handleCreateForm = async (form: UserCreateFormData) => {
        const dto: CreateUserRequestDto = {
            userName: form.userName,
            password: form.password,
            lockoutEnabled: form.lockoutEnabled || false,
            isSuperAdmin: form.isSuperAdmin || false,
            tenantRoles: form.tenantRoles,
        };

        await createUser(dto);
        handleClose();
    };

    const handleUpdateForm = async (form: UserUpdateFormData) => {
        const dto: UpdateUserRequestDto = {
            userName: form.userName,
            lockoutEnabled: form.lockoutEnabled || false,
            emailConfirmed: form.emailConfirmed || false,
            isSuperAdmin: form.isSuperAdmin || false,
            tenantRoles: form.tenantRoles,
        };

        await updateUser(dto);
        handleClose();
    };

    const requiresSuperAdminConfirmation = (isSuperAdmin?: boolean) =>
        Boolean(isSuperAdmin) && (!id || !initialIsSuperAdminRef.current);

    const submitForm = (form: UserUpdateFormData | UserCreateFormData) => {
        if (id) {
            void handleUpdateForm(form as UserUpdateFormData);
        } else {
            void handleCreateForm(form as UserCreateFormData);
        }
    };

    const handleSubmitForm = () => {
        handleSubmit((form) => {
            if (requiresSuperAdminConfirmation(form.isSuperAdmin)) {
                setConfirmDialogOpen(true);
                return;
            }

            submitForm(form);
        })();
    };

    const handleConfirmSuperAdmin = () => {
        handleSubmit((form) => submitForm(form))();
    };

    const canImpersonate =
        !!id && !!userDetailsData && !userDetailsData.isSuperAdmin;

    const impersonateDisabled = !userDetailsData?.isMigratedToKeycloak;

    const canSendRecoveryEmail =
        !!userDetailsData?.userName &&
        userDetailsData.emailConfirmed &&
        !userDetailsData.lockoutEnabled;

    const recoveryEmailDisabledReason = !userDetailsData?.userName
        ? t('main.superAdmin.users.components.details.actions.sendPasswordRecovery.disabledNoEmail')
        : !userDetailsData.emailConfirmed
          ? t('main.superAdmin.users.components.details.actions.sendPasswordRecovery.disabledNotConfirmed')
          : userDetailsData.lockoutEnabled
            ? t('main.superAdmin.users.components.details.actions.sendPasswordRecovery.disabledLockedOut')
            : undefined;

    const handleSetPassword = async (newPassword: string) => {
        await setUserPassword({ newPassword });
    };

    const handleSendPasswordRecovery = async () => {
        await sendPasswordRecoveryEmail();
    };

    const handleImpersonate = async () => {
        if (!canImpersonate || impersonateDisabled) return;

        const loginResponse = await impersonateUser();
        const claims = AuthHelper.parseJwtTokenClaims(loginResponse.tokens.accessToken);
        const user = AuthHelper.createUser(loginResponse.user, claims);

        if (!user) {
            EToast({
                variant: 'error',
                message: t('main.superAdmin.users.toasts.impersonate.error.message'),
                description: t('main.superAdmin.users.toasts.impersonate.error.description'),
            });
            return;
        }

        signIn({
            tokens: loginResponse.tokens,
            user,
        });

        handleClose();
        navigate('/auth/choose-hotel', { replace: true });
    };

    return (
        <>
            <Sheet open={isOpen} onOpenChange={handleClose}>
                <SheetContent className={cn('flex flex-col', isMobile ? 'w-full' : 'w-160')}>
                    <SheetHeader>
                        <SheetDescription />
                        <SheetTitle>
                            {!isMobile && (
                                <SheetClose
                                    className={cn(
                                        'flex items-center justify-center cursor-pointer',
                                        isMobile && 'size-11 border border-popover-100 rounded-full',
                                    )}
                                >
                                    <MoveLeftIcon />
                                </SheetClose>
                            )}
                            {t('main.superAdmin.users.components.details.title')}
                            {isMobile && (
                                <SheetClose className="size-11 ml-auto flex items-center justify-center cursor-pointer border border-popover-100 rounded-full">
                                    <Cancel_01_Icon />
                                </SheetClose>
                            )}
                        </SheetTitle>
                    </SheetHeader>
                    <div className="flex-1 flex flex-col bg-popover-30 overflow-hidden select-none">
                        <div
                            className={cn(
                                'h-full p-6 flex flex-col gap-6 overflow-y-scroll no-scrollbar',
                                isMobile && 'p-4',
                            )}
                        >
                            {!!id && (
                                <div
                                    className={
                                        'py-10 px-6 flex flex-col items-center gap-4 rounded-2.5xl bg-popover-10 border border-popover-100'
                                    }
                                >
                                    <div className="flex flex-col items-center justify-center gap-4 text-center">
                                        <h3
                                            className={cn(
                                                'text-lg font-medium',
                                                isMobile && 'text-base',
                                            )}
                                        >
                                            {userDetailsData?.userName}
                                        </h3>
                                        <ECreateDetailsCopyItem
                                            type="ID"
                                            className={cn(isMobile && 'justify-between')}
                                        >
                                            {userDetailsData?.id}
                                        </ECreateDetailsCopyItem>
                                    </div>
                                </div>
                            )}
                            <div className="flex flex-col gap-2">
                                <h3 className={cn(isMobile && 'text-base')}>
                                    {t('main.superAdmin.users.components.details.list.title')}
                                </h3>
                                <div
                                    className={cn(
                                        'p-6 flex flex-col gap-4 border border-popover-100 rounded-2.5xl bg-popover-10',
                                        isMobile && 'p-0 border-0 bg-popover-30',
                                    )}
                                >
                                    <ETextInput
                                        control={control}
                                        fieldName="userName"
                                        label={t('main.superAdmin.users.form.userName.title')}
                                        placeholder={t(
                                            'main.superAdmin.users.form.userName.placeholder',
                                        )}
                                    />
                                    {!id && (
                                        <UserPasswordField
                                            control={control as Control<UserCreateFormData>}
                                            setValue={setValue as UseFormSetValue<UserCreateFormData>}
                                        />
                                    )}
                                    <div className="flex gap-5">
                                        <ECheckboxInput
                                            control={control}
                                            fieldName="lockoutEnabled"
                                            text={t('main.superAdmin.users.form.lockoutEnabled.title')}
                                            containerClassName={id ? 'w-1/2' : 'w-full'}
                                        />
                                        {!!id && (
                                            <ECheckboxInput
                                                control={control}
                                                fieldName="emailConfirmed"
                                                text={t(
                                                    'main.superAdmin.users.form.emailConfirmed.title',
                                                )}
                                                containerClassName="w-1/2"
                                            />
                                        )}
                                    </div>
                                    <ECheckboxInput
                                        control={control}
                                        fieldName="isSuperAdmin"
                                        text={t('main.superAdmin.users.form.isSuperAdmin.title')}
                                    />
                                </div>
                            </div>
                            <div className="flex flex-col gap-2">
                                <h3 className={cn(isMobile && 'text-base')}>
                                    {t('main.superAdmin.users.form.tenantRoles.title')}
                                </h3>
                                <div
                                    className={cn(
                                        'p-6 flex flex-col gap-4 border border-popover-100 rounded-2.5xl bg-popover-10',
                                        isMobile && 'p-0 border-0 bg-popover-30',
                                    )}
                                >
                                    <UserTenantRoles control={control} fieldName="tenantRoles" />
                                </div>
                            </div>
                        </div>
                    </div>
                    <SheetFooter className="flex flex-col gap-2">
                        {!!id && (
                            <>
                                <EButton
                                    className="w-full"
                                    variant="outline"
                                    onClick={() => setSetPasswordDialogOpen(true)}
                                >
                                    {t('main.superAdmin.users.components.details.actions.setPassword.button')}
                                </EButton>
                                <EButton
                                    className="w-full"
                                    variant="outline"
                                    disabled={!canSendRecoveryEmail}
                                    tooltipText={recoveryEmailDisabledReason}
                                    onClick={() => setRecoveryDialogOpen(true)}
                                >
                                    {t(
                                        'main.superAdmin.users.components.details.actions.sendPasswordRecovery.button',
                                    )}
                                </EButton>
                            </>
                        )}
                        {canImpersonate && (
                            <EButton
                                className="w-full"
                                variant="outline"
                                disabled={impersonateDisabled}
                                isLoading={isImpersonatingUser}
                                tooltipText={
                                    impersonateDisabled
                                        ? t(
                                              'main.superAdmin.users.components.details.buttons.impersonateDisabledTooltip',
                                          )
                                        : undefined
                                }
                                onClick={() => void handleImpersonate()}
                            >
                                {t('main.superAdmin.users.components.details.buttons.impersonate')}
                            </EButton>
                        )}
                        <Button className="w-full" onClick={handleSubmitForm}>
                            {id
                                ? t('main.superAdmin.users.components.details.buttons.update')
                                : t('main.superAdmin.users.components.details.buttons.create')}
                        </Button>
                    </SheetFooter>
                    <Toaster />
                </SheetContent>
            </Sheet>
            <UserSuperAdminConfirmDialog
                open={confirmDialogOpen}
                onOpenChange={setConfirmDialogOpen}
                onConfirm={handleConfirmSuperAdmin}
                isSubmitting={isCreatingUser || isUpdatingUser}
            />
            <UserSetPasswordDialog
                open={setPasswordDialogOpen}
                onOpenChange={setSetPasswordDialogOpen}
                onSubmit={handleSetPassword}
                isSubmitting={isSettingUserPassword}
            />
            <UserSendPasswordRecoveryDialog
                open={recoveryDialogOpen}
                onOpenChange={setRecoveryDialogOpen}
                userEmail={userDetailsData?.userName}
                onConfirm={handleSendPasswordRecovery}
                isSubmitting={isSendingPasswordRecoveryEmail}
            />
        </>
    );
};

export default UserManage;
